import type { ConversationMessage, LearningSession, SceneTheme } from "@shared/types";

const openings: Record<string, Record<SceneTheme, string>> = {
  en: {
    home: "Good morning! I'm AURA. We're spending a morning at home together. How is your day going?",
    office: "Hello! I'm AURA. We're in a futuristic office. How would you present an idea in a meeting?",
    airport: "Hello! I'm AURA. We're at the airport. Where would you like to travel today?",
    restaurant: "Hello! I'm AURA. We're in a restaurant. What would you like to order?",
    park: "Hello! I'm AURA. We're walking through a park. What do you usually do to relax?",
    hotel: "Hello! I'm AURA. We're at a hotel reception. How can I help with your stay?",
    interview: "Hello! I'm AURA. Let's practice a job interview. How would you introduce yourself?",
    paris_museum: "Hello! I'm AURA. We're in a museum in Paris. Which artwork would you like to observe first?",
    custom: "Hello! I'm AURA. I'm ready to explore this scenario with you. Where would you like to begin?"
  },
  es: {
    home: "¡Buenos días! Soy AURA. Estamos pasando una mañana en casa. ¿Cómo va tu día?",
    office: "¡Hola! Soy AURA. Estamos en una oficina futurista. ¿Cómo presentarías una idea en una reunión?",
    airport: "¡Hola! Soy AURA. Estamos en el aeropuerto. ¿Adónde te gustaría viajar hoy?",
    restaurant: "¡Hola! Soy AURA. Estamos en un restaurante. ¿Qué te gustaría pedir?",
    park: "¡Hola! Soy AURA. Estamos caminando por un parque. ¿Qué haces normalmente para relajarte?",
    hotel: "¡Hola! Soy AURA. Estamos en la recepción de un hotel. ¿Cómo puedo ayudarte con tu estancia?",
    interview: "¡Hola! Soy AURA. Practiquemos una entrevista de trabajo. ¿Cómo te presentarías?",
    paris_museum: "¡Hola! Soy AURA. Estamos en un museo de París. ¿Qué obra te gustaría observar primero?",
    custom: "¡Hola! Soy AURA. Estoy lista para explorar este escenario contigo. ¿Por dónde te gustaría empezar?"
  },
  fr: {
    home: "Bonjour ! Je suis AURA. Nous passons une matinée à la maison. Comment se passe votre journée ?",
    office: "Bonjour ! Je suis AURA. Nous sommes dans un bureau futuriste. Comment présenteriez-vous une idée en réunion ?",
    airport: "Bonjour ! Je suis AURA. Nous sommes à l'aéroport. Où aimeriez-vous voyager aujourd'hui ?",
    restaurant: "Bonjour ! Je suis AURA. Nous sommes au restaurant. Qu'aimeriez-vous commander ?",
    park: "Bonjour ! Je suis AURA. Nous nous promenons dans un parc. Que faites-vous habituellement pour vous détendre ?",
    hotel: "Bonjour ! Je suis AURA. Nous sommes à la réception d'un hôtel. Comment puis-je vous aider pour votre séjour ?",
    interview: "Bonjour ! Je suis AURA. Simulons un entretien d'embauche. Comment vous présenteriez-vous ?",
    paris_museum: "Bonjour ! Je suis AURA. Nous sommes dans un musée à Paris. Quelle œuvre aimeriez-vous observer en premier ?",
    custom: "Bonjour ! Je suis AURA. Je suis prête à explorer ce scénario avec vous. Par où souhaitez-vous commencer ?"
  },
  zh: {
    home: "早上好！我是 AURA。我们正在家里度过一个早晨。你今天过得怎么样？",
    office: "你好！我是 AURA。我们在一间未来办公室里。你会怎样在会议中介绍一个想法？",
    airport: "你好！我是 AURA。我们在机场。你今天想去哪里旅行？",
    restaurant: "你好！我是 AURA。我们在餐厅。你想点什么？",
    park: "你好！我是 AURA。我们正在公园里散步。你通常怎样放松？",
    hotel: "你好！我是 AURA。我们在酒店前台。我可以怎样帮助你办理住宿？",
    interview: "你好！我是 AURA。我们来练习求职面试。你会怎样介绍自己？",
    paris_museum: "你好！我是 AURA。我们在巴黎的一家博物馆。你想先看哪件作品？",
    custom: "你好！我是 AURA。我已经准备好和你一起探索这个场景。你想从哪里开始？"
  }
};

const translations: Record<SceneTheme, string> = {
  home: "Bom dia! Eu sou AURA. Vamos praticar juntos nesta manhã em família. Como está o seu dia hoje?",
  office: "Olá! Eu sou AURA. Estamos em um escritório futurista. Como você apresentaria uma ideia em uma reunião?",
  airport: "Olá! Eu sou AURA. Estamos no aeroporto. Para onde você gostaria de viajar hoje?",
  restaurant: "Olá! Eu sou AURA. Estamos em um restaurante. O que você gostaria de pedir?",
  park: "Olá! Eu sou AURA. Estamos passeando em um parque. O que você costuma fazer para relaxar?",
  hotel: "Olá! Eu sou AURA. Estamos na recepção de um hotel. Como posso ajudar com a sua hospedagem?",
  interview: "Olá! Eu sou AURA. Vamos simular uma entrevista. Como você se apresentaria?",
  paris_museum: "Bonjour! Eu sou AURA. Estamos em um museu de Paris. Qual obra você gostaria de observar primeiro?",
  custom: "Olá! Eu sou AURA. Estou pronta para explorar este cenário com você. Por onde gostaria de começar?"
};

export function buildScenarioOpeningPreview(session: LearningSession): ConversationMessage {
  const languageSet = openings[session.targetLanguage] || openings.en;
  const theme = session.sceneTheme || "custom";
  return {
    id: -Math.max(1, session.id),
    sessionId: session.id,
    speaker: "assistant",
    text: languageSet[theme] || languageSet.custom,
    translation: session.targetLanguage === "en" ? null : translations[theme],
    correction: null,
    emotion: "welcoming",
    sceneAction: session.sceneAction,
    createdAt: new Date().toISOString()
  };
}
